import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { z } from "zod";
import { findAirport } from "@/lib/airports";
import { formatDisplayPrice } from "@/lib/price-display";

export type PriceDrop = {
  from: string;
  to: string;
  fromCity: string;
  toCity: string;
  toCountry: string;
  price: string;
  priceNumeric: number;
  /** Data lotu (YYYY-MM-DD), dla której obowiązuje cena z Aviasales. */
  departDate?: string;
  avg30d: number;
  dropPct: number; // 0..1, ile % taniej vs avg30d
};

const InputSchema = z.object({
  limit: z.number().int().min(1).max(24).optional().default(8),
  minPctDrop: z.number().min(0).max(1).optional().default(0.15),
});

/**
 * Globalne "spadki cen": dla każdej trasy z route_prices liczymy avg30d
 * z route_price_history i zwracamy te z największą procentową obniżką.
 * Filtrujemy do tras z Polski (główna grupa docelowa).
 */
export const getBiggestDrops = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => InputSchema.parse(input))
  .handler(async ({ data }): Promise<PriceDrop[]> => {
    try {
      const url = process.env.SUPABASE_URL ?? process.env.VITE_SUPABASE_URL;
      const key =
        process.env.SUPABASE_SERVICE_ROLE_KEY ??
        process.env.SUPABASE_PUBLISHABLE_KEY ??
        process.env.SUPABASE_ANON_KEY ??
        process.env.VITE_SUPABASE_PUBLISHABLE_KEY;
      if (!url || !key) return [];
      const sb = createClient(url, key, {
        auth: { persistSession: false, autoRefreshToken: false },
      });

      const { data: current } = await sb
        .from("route_prices")
        .select("from_iata,to_iata,display_price,depart_date")
        .order("display_price", { ascending: true })
        .limit(400);
      if (!current || current.length === 0) return [];

      // Filtr: tylko z Polski + sensowne ceny.
      const valid = current.filter((r) => {
        if (typeof r.display_price !== "number" || r.display_price <= 0) return false;
        const af = findAirport(r.from_iata);
        return af?.country === "Polska";
      });
      if (valid.length === 0) return [];

      const since = new Date(Date.now() - 30 * 24 * 3600 * 1000).toISOString();
      // Batch w paczkach po ~100, żeby uniknąć ograniczeń długości IN.
      const stats = new Map<string, { sum: number; n: number }>();
      const batchSize = 80;
      for (let i = 0; i < valid.length; i += batchSize) {
        const slice = valid.slice(i, i + batchSize);
        const fromIatas = [...new Set(slice.map((r) => r.from_iata))];
        const toIatas = [...new Set(slice.map((r) => r.to_iata))];
        const { data: hist } = await sb
          .from("route_price_history")
          .select("from_iata,to_iata,display_price,depart_date")
          .gte("checked_at", since)
          .in("from_iata", fromIatas)
          .in("to_iata", toIatas);
        for (const h of hist ?? []) {
          if (typeof h.display_price !== "number" || h.display_price <= 0) continue;
          const k = `${h.from_iata}-${h.to_iata}`;
          const ex = stats.get(k) ?? { sum: 0, n: 0 };
          ex.sum += h.display_price;
          ex.n += 1;
          stats.set(k, ex);
        }
      }

      const dropsByPair = new Map<string, PriceDrop>();
      for (const r of valid) {
        const k = `${r.from_iata}-${r.to_iata}`;
        const s = stats.get(k);
        if (!s || s.n < 3) continue; // za mało danych
        const avg = s.sum / s.n;
        const dropPct = (avg - r.display_price) / avg;
        if (dropPct < data.minPctDrop) continue;
        const af = findAirport(r.from_iata);
        const at = findAirport(r.to_iata);
        if (!af || !at) continue;
        const cityKey = `${af.city}|${at.city}`;
        const ex = dropsByPair.get(cityKey);
        if (ex && ex.dropPct >= dropPct) continue;
        dropsByPair.set(cityKey, {
          from: r.from_iata,
          to: r.to_iata,
          fromCity: af.city,
          toCity: at.city,
          toCountry: at.country,
          price: formatDisplayPrice(r.display_price),
          priceNumeric: r.display_price,
          departDate: (r as { depart_date?: string | null }).depart_date ?? undefined,
          avg30d: Math.round(avg),
          dropPct,
        });
      }

      return [...dropsByPair.values()]
        .sort((a, b) => b.dropPct - a.dropPct)
        .slice(0, data.limit);
    } catch {
      return [];
    }
  });
